import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, StudentError } from "../studentClient.js";
import { badgeClass } from "../../admin/badge.js";

interface Current {
  quizId: string; title: string; open: boolean; windowDate: string;
  total: number; status: string; score: number | null;
}
interface Hist { quizId: string; windowDate: string; status: string; score: number | null; total: number | null; submittedAt: string | null; }

/** "56 / 77" for a graded attempt; falls back to the bare score if the set's
 * question count is unavailable, and "-" when there's nothing to show. */
const scoreText = (h: Pick<Hist, "status" | "score" | "total">): string => {
  if (h.status !== "submitted" || h.score == null) return "-";
  return h.total ? `${h.score} / ${h.total}` : String(h.score);
};

const setLabel = (quizId: string) => {
  const m = /^set-(\d+)$/.exec(quizId);
  return m ? `Set ${m[1]}` : quizId;
};
const fmtDate = (d: string) => {
  const dt = new Date(`${d}T00:00:00`);
  return Number.isNaN(dt.getTime()) ? d : dt.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};
const statusText = (s: string) => (s === "submitted" ? "Submitted" : s === "in-progress" ? "In progress" : s);
const statusBadge = (s: string) => badgeClass(s === "submitted" ? "Passed" : "Pending");

export default function QuizzesPage() {
  const nav = useNavigate();
  const [current, setCurrent] = useState<Current | null>(null);
  const [history, setHistory] = useState<Hist[]>([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api<{ current: Current | null; history: Hist[] }>("/student/quizzes")
      .then((d) => { setCurrent(d.current); setHistory(d.history); })
      .catch((e) => setError(e instanceof StudentError ? e.message : "Failed to load quizzes"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="subhead">Loading...</p>;
  if (error) return <div className="login-err">{error}</div>;

  return (
    <>
      <h1>Quizzes</h1>
      <p className="subhead">The weekly quiz opens Thursday 7:00 PM and closes Friday 9:00 PM (Chicago time).</p>

      <div className="card pad" style={{ marginBottom: 16 }}>
        <div className="alabel" style={{ marginTop: 0 }}>This week</div>
        {!current ? (
          <p className="sub">No quiz scheduled right now. Check back Thursday evening.</p>
        ) : (
          <>
            <div className="row">
              <div className="nm">{current.title}</div>
              {current.status === "submitted"
                ? <span className={badgeClass("Passed")}>Submitted{current.score != null ? ` · ${current.score}/${current.total}` : ""}</span>
                : <span className={badgeClass(current.open ? "Active" : "Upcoming")}>{current.open ? "Open now" : "Not open yet"}</span>}
            </div>
            <div className="actions" style={{ marginTop: 12 }}>
              {current.status === "submitted" ? (
                <button className="rbtn" onClick={() => nav(`/portal/quiz/${current.quizId}`)}>View results</button>
              ) : current.open ? (
                <button className="rbtn coral" onClick={() => nav(`/portal/quiz/${current.quizId}`)}>
                  {current.status === "in-progress" ? "Resume quiz" : "Start quiz"}
                </button>
              ) : (
                <p className="sub">Opens Thursday 7:00 PM.</p>
              )}
            </div>
          </>
        )}
      </div>

      <div className="alabel">History</div>
      {history.length === 0 ? (
        <div className="card empty">No quizzes taken yet.</div>
      ) : (
        <>
          <p className="sub" style={{ marginTop: 0 }}>Tap a submitted quiz to review your answers.</p>
          <table>
            <thead><tr><th>Quiz</th><th>Date</th><th>Score</th><th>Status</th></tr></thead>
            <tbody>
              {history.map((h) => {
                const reviewable = h.status === "submitted";
                const open = () => reviewable && nav(`/portal/quiz/${h.quizId}/result/${h.windowDate}`);
                return (
                  <tr
                    key={`${h.quizId}#${h.windowDate}`}
                    className={reviewable ? "clickable" : ""}
                    style={reviewable ? undefined : { cursor: "default" }}
                    onClick={open}
                  >
                    <td><div className="nm">{setLabel(h.quizId)}</div></td>
                    <td className="sub">{fmtDate(h.windowDate)}</td>
                    <td>{scoreText(h)}</td>
                    <td><span className={statusBadge(h.status)}>{statusText(h.status)}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="cards-list">
            {history.map((h) => {
              const reviewable = h.status === "submitted";
              return (
                <div
                  key={`${h.quizId}#${h.windowDate}`}
                  className={`card pad${reviewable ? " clickable" : ""}`}
                  style={{ marginBottom: 10 }}
                  onClick={() => reviewable && nav(`/portal/quiz/${h.quizId}/result/${h.windowDate}`)}
                >
                  <div className="row"><div className="nm">{setLabel(h.quizId)}</div><span className={statusBadge(h.status)}>{statusText(h.status)}</span></div>
                  <div className="sub" style={{ marginTop: 4 }}>{fmtDate(h.windowDate)} · Score: {scoreText(h)}{reviewable ? " · Tap to review" : ""}</div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </>
  );
}
